import "@/App.css";
import React from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { Loader } from "@/components/kit";
import DashboardLayout from "@/components/DashboardLayout";

import Landing from "@/pages/Landing";
import Auth from "@/pages/Auth";
import Onboarding from "@/pages/Onboarding";
import Dashboard from "@/pages/Dashboard";
import Create from "@/pages/Create";
import Menus from "@/pages/Menus";
import MenuDetail from "@/pages/MenuDetail";
import Recipes from "@/pages/Recipes";
import PlateStudio from "@/pages/PlateStudio";
import PCA from "@/pages/PCA";
import Purchasing from "@/pages/Purchasing";
import MyKitchen from "@/pages/MyKitchen";
import ProfitMode from "@/pages/ProfitMode";
import Analytics from "@/pages/Analytics";
import Team from "@/pages/Team";
import Settings from "@/pages/Settings";
import Pricing from "@/pages/Pricing";
import Admin from "@/pages/Admin";
import KitchenMode from "@/pages/KitchenMode";
import PaymentSuccess from "@/pages/PaymentSuccess";
import VerifyEmail from "@/pages/VerifyEmail";
import SharedMenu from "@/pages/SharedMenu";
import Legal from "@/pages/Legal";

function Protected({ children }) {
  const { user, loading } = useAuth();
  if (loading || user === null) return <div className="min-h-screen bg-ink"><Loader label="Preparing your station…" /></div>;
  if (!user) return <Navigate to="/login" replace />;
  if (!user.profile?.completed) return <Navigate to="/onboarding" replace />;
  return children;
}

function Shell({ children }) {
  return <Protected><DashboardLayout>{children}</DashboardLayout></Protected>;
}

function RootRoute() {
  const { user, loading } = useAuth();
  if (loading || user === null) return <div className="min-h-screen bg-ink"><Loader label="Loading…" /></div>;
  if (user) return <Navigate to={user.profile?.completed ? "/dashboard" : "/onboarding"} replace />;
  return <Landing />;
}

function App() {
  return (
    <div className="App">
      <AuthProvider>
        <Toaster theme="dark" position="top-right" toastOptions={{
          style: { background: "#141414", border: "1px solid rgba(212,175,55,0.3)", color: "#F5F5F0" },
        }} />
        <BrowserRouter>
          <Routes>
            <Route path="/" element={<RootRoute />} />
            <Route path="/login" element={<Auth mode="login" />} />
            <Route path="/signup" element={<Auth mode="signup" />} />
            <Route path="/forgot-password" element={<Auth mode="forgot" />} />
            <Route path="/reset-password" element={<Auth mode="reset" />} />
            <Route path="/onboarding" element={<Protected><Onboarding /></Protected>} />
            <Route path="/pricing" element={<Pricing />} />
            <Route path="/payment/success" element={<PaymentSuccess />} />
            <Route path="/verify-email" element={<VerifyEmail />} />
            <Route path="/shared-menu/:token" element={<SharedMenu />} />
            <Route path="/terms" element={<Legal doc="terms" />} />
            <Route path="/privacy" element={<Legal doc="privacy" />} />
            <Route path="/data" element={<Legal doc="data" />} />

            <Route path="/dashboard" element={<Shell><Dashboard /></Shell>} />
            <Route path="/create" element={<Shell><Create /></Shell>} />
            <Route path="/menus" element={<Shell><Menus /></Shell>} />
            <Route path="/menus/:id" element={<Shell><MenuDetail /></Shell>} />
            <Route path="/recipes" element={<Shell><Recipes /></Shell>} />
            <Route path="/plate-studio" element={<Shell><PlateStudio /></Shell>} />
            <Route path="/pca" element={<Shell><PCA /></Shell>} />
            <Route path="/purchasing" element={<Shell><Purchasing /></Shell>} />
            <Route path="/my-kitchen" element={<Shell><MyKitchen /></Shell>} />
            <Route path="/profit" element={<Shell><ProfitMode /></Shell>} />
            <Route path="/analytics" element={<Shell><Analytics /></Shell>} />
            <Route path="/team" element={<Shell><Team /></Shell>} />
            <Route path="/settings" element={<Shell><Settings /></Shell>} />
            <Route path="/admin" element={<Shell><Admin /></Shell>} />
            <Route path="/kitchen-mode/:id" element={<Protected><KitchenMode /></Protected>} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </BrowserRouter>
      </AuthProvider>
    </div>
  );
}

export default App;
